import { builtInTool } from '@zerohuman/shared'
import type { SettledOutcome } from '@zerohuman/ui'
import { type ChatGateState, formatAgo } from '@/api'

/**
 * A decided gate as its chat card shows it for good (#1981): how it ended, in the words its state says, the lines
 * under its title (each answer, then the note), and how long ago it was decided. Built from the thread's own read
 * of the gate alone, so a card decided on Gates, in chat or anywhere else reads exactly the same.
 */
export type SettledGateCard = {
    outcome: SettledOutcome
    /** Said in place of the outcome's own word: the choice it was decided through, or how a gate ended without one. */
    label?: string
    lines: string[]
    when?: string
}

/** The gate tool that asks the CEO questions: approving it is answering them. */
const ASK_CEO = 'os.ask_ceo'

/**
 * How a gate ended, as its card's outcome: null while it waits. An approved question was answered; a gate that
 * ended without a decision (it timed out, or was cancelled with its run) is `closed`, and says so in words.
 */
export function settledOutcome(gate: Pick<ChatGateState, 'status' | 'toolName' | 'answers'>): SettledOutcome | null {
    switch (gate.status) {
        case 'waiting':
            return null
        case 'approved':
            return gate.toolName === ASK_CEO || (gate.answers?.length ?? 0) > 0 ? 'answered' : 'approve'
        case 'rejected':
            return 'reject'
        case 'changes_requested':
            return 'request_changes'
        default:
            return 'closed'
    }
}

/** A status as a person reads it: `cancelled` is "Cancelled", `timed_out` "Timed out". */
function statusWords(status: string): string {
    const words = status.replace(/_/g, ' ').trim()
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Closed'
}

/**
 * The note as the decision asked for it: under the choice's own label when the choice asked for something (a
 * snooze's date and time, read as one), otherwise as the decision's note.
 */
function noteLine(gate: Pick<ChatGateState, 'toolName' | 'choiceLabel'>, note: string): string {
    const input = gate.choiceLabel
        ? builtInTool(gate.toolName)?.gateChoices?.find(choice => choice.label === gate.choiceLabel)?.input
        : undefined
    if (!input) {
        return `Note: ${note}`
    }
    const at = input.kind === 'datetime' ? Date.parse(note) : Number.NaN
    const shown = Number.isNaN(at)
        ? note
        : new Date(at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    // What the form asked, without what only mattered while filling it in: "Reason (optional)" reads "Reason".
    const asked = input.label.replace(/\s*\(optional\)\s*$/i, '')
    return asked.endsWith('?') ? `${asked} ${shown}` : `${asked}: ${shown}`
}

/**
 * A decided gate's card, or null while it still waits. Each answer reads as the thread's "Decision recorded" message
 * writes it (`1. Which repo? — The main one.`, or `needs more detail`), and the note follows them.
 */
export function settledGateCard(gate: ChatGateState, now: number): SettledGateCard | null {
    const outcome = settledOutcome(gate)
    if (!outcome) {
        return null
    }
    const lines = (gate.answers ?? []).map(answer => {
        const reply = answer.requiresMoreDetail ? 'needs more detail' : answer.answer
        return answer.question ? `${answer.number}. ${answer.question} — ${reply}` : `${answer.number}. ${reply}`
    })
    const note = gate.decisionNote?.trim()
    if (note) {
        lines.push(noteLine(gate, note))
    }
    const label = gate.choiceLabel ?? (outcome === 'closed' ? statusWords(gate.status) : undefined)
    const decidedAt = gate.decidedAt ? Date.parse(gate.decidedAt) : Number.NaN
    return {
        outcome,
        ...(label ? { label } : {}),
        lines,
        ...(Number.isNaN(decidedAt) ? {} : { when: formatAgo(Math.max(0, now - decidedAt)) }),
    }
}
